// Counselor caseload-dashboard. PRD-04 §3.1.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CounselorLayout } from "@/components/CounselorLayout";

interface ClientRow {
  id: string;
  name: string;
  email: string | null;
  lastLoginAt: string | null;
  language: string;
  onboarding: {
    status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "ABANDONED";
    currentStep: number;
    completedAt: string | null;
  } | null;
  /** Plaats binnen déze lijst, bepaald door de server. */
  rang: number;
  triageScore: number;
  redenen: Array<{ sleutel: string; gewicht: number; omschrijving: string }>;
  /** Door de counselor zelf gezet; overstemt de berekende volgorde. */
  bijstelling: { richting: "EERDER" | "LATER"; reden: string; vervaltOp: string } | null;
  threats: { critical: number; high: number; medium: number; total: number };
  createdAt: string;
}

export function CaseloadPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["caseload", search],
    queryFn: () =>
      api.get<{ clients: ClientRow[] }>(
        `/v1/counselor/caseload${search ? `?search=${encodeURIComponent(search)}` : ""}`
      ),
    refetchInterval: 60_000,
  });

  // De server bepaalt de volgorde en levert hem al gesorteerd aan. Hier
  // nogmaals sorteren zou een tweede, afwijkende waarheid opleveren.
  const sorted = query.data?.clients ?? [];

  /**
   * De counselor stelt de volgorde bij. De reden is verplicht en dat is geen
   * formaliteit: de weging is nooit geijkt, en deze bijstellingen zijn over een
   * half jaar de enige meting die we hebben van waar het systeem ernaast zat.
   */
  const bijstellen = useMutation({
    mutationFn: ({ id, richting, reden }: { id: string; richting: "EERDER" | "LATER"; reden: string }) =>
      api.post(`/v1/counselor/clients/${id}/triage-bijstelling`, { richting, reden }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caseload"] }),
  });

  const intrekken = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/counselor/clients/${id}/triage-bijstelling`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caseload"] }),
  });

  // Een teller die op vrijwel iedereen slaat, telt niets. "Urgent" deed dat
  // (alles vanaf score 40, in de praktijk negen van de tien), en mijn eerste
  // vervanging "met signalen" bleek precies zo leeg: bij vijftig oefen-dossiers
  // stond er vijftig van vijftig. Vandaar iets wat wél verschilt per dag en
  // waar bovendien een handeling aan vastzit.
  const wachtOpJou = sorted.filter((c) =>
    c.redenen.some((r) => r.sleutel === "pendingConcepts")
  );

  return (
    <CounselorLayout title={t("counselor.caseload.title", "Caseload")}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard
          label={t("counselor.caseload.totalClients", "Totaal clienten")}
          value={sorted.length}
        />
        <SummaryCard
          label={t("counselor.caseload.awaitingYou", "Wacht op jou")}
          value={wachtOpJou.length}
          tone={wachtOpJou.length > 0 ? "amber" : "neutral"}
        />
        <SummaryCard
          label={t("counselor.caseload.activeOnboarding", "In onboarding")}
          value={sorted.filter((c) => c.onboarding?.status === "IN_PROGRESS").length}
          tone="amber"
        />
        <SummaryCard
          label={t("counselor.caseload.completed", "Onboarding klaar")}
          value={sorted.filter((c) => c.onboarding?.status === "COMPLETED").length}
          tone="green"
        />
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <header className="px-5 py-3 border-b border-neutral-200 flex items-center gap-3">
          <input
            type="search"
            placeholder={t("counselor.caseload.search", "Zoek client op naam")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 max-w-xs px-3 py-2 border border-neutral-300 rounded-lg text-sm"
          />
        </header>

        {query.isLoading ? (
          <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
        ) : sorted.length === 0 ? (
          <p className="p-5 text-sm text-neutral-500">
            {t("counselor.caseload.empty", "Geen clienten in je caseload.")}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-center px-3 py-2 font-medium">{t("counselor.caseload.col.rank", "#")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("counselor.caseload.col.name", "Naam")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("counselor.caseload.col.reasons", "Waarom hier")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("counselor.caseload.col.email", "E-mail")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("counselor.caseload.col.onboarding", "Onboarding")}</th>
                <th className="text-center px-3 py-2 font-medium">{t("counselor.caseload.col.threats", "Dreigingen")}</th>
                <th className="text-left px-5 py-2 font-medium">{t("counselor.caseload.col.lastLogin", "Laatste login")}</th>
                <th className="text-right px-5 py-2 font-medium">{t("counselor.caseload.col.order", "Volgorde")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2.5 text-center align-top">
                    <span className="inline-block min-w-[1.75rem] px-2 py-0.5 rounded text-xs font-mono bg-neutral-100 text-neutral-600">
                      {c.rang}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 align-top">
                    <Link
                      to={`/counselor/clients/${c.id}`}
                      className="font-medium text-brand-700 hover:text-brand-900"
                    >
                      {c.name}
                    </Link>
                  </td>
                  <td className="px-5 py-2.5 align-top">
                    <Redenen redenen={c.redenen} />
                  </td>
                  <td className="px-5 py-2.5 text-neutral-600 text-xs">{c.email}</td>
                  <td className="px-5 py-2.5 text-xs">
                    {c.onboarding ? (
                      c.onboarding.status === "COMPLETED" ? (
                        <span className="text-green-700">✓ Klaar</span>
                      ) : (
                        <span className="text-amber-700">
                          Stap {c.onboarding.currentStep} / 9
                        </span>
                      )
                    ) : (
                      <span className="text-neutral-400">–</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <ThreatPills counts={c.threats} />
                  </td>
                  <td className="px-5 py-2.5 text-xs text-neutral-500 align-top">
                    {c.lastLoginAt
                      ? new Date(c.lastLoginAt).toLocaleDateString("nl-NL", {
                          dateStyle: "short",
                        })
                      : t("counselor.caseload.never", "Nog niet")}
                  </td>
                  <td className="px-5 py-2.5 text-right align-top">
                    <VolgordeKnoppen
                      rij={c}
                      bezig={bijstellen.isPending || intrekken.isPending}
                      onBijstellen={(richting, reden) =>
                        bijstellen.mutate({ id: c.id, richting, reden })
                      }
                      onIntrekken={() => intrekken.mutate(c.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </CounselorLayout>
  );
}

/**
 * Waarmee de counselor de volgorde bijstelt.
 *
 * Twee knoppen en niet een sleepbare lijst, met opzet. "Hier begin ik vandaag"
 * en "deze niet nu" zijn uitspraken die een counselor werkelijk doet; een
 * precieze positie tussen plaats 14 en 15 is dat niet, en die zou als meting
 * ook niets betekenen.
 *
 * De reden is verplicht. Dat kost een handeling extra en dat is de bedoeling:
 * over een half jaar is juist die reden het enige wat vertelt waar de weging
 * ernaast zat.
 */
function VolgordeKnoppen({
  rij,
  bezig,
  onBijstellen,
  onIntrekken,
}: {
  rij: ClientRow;
  bezig: boolean;
  onBijstellen: (richting: "EERDER" | "LATER", reden: string) => void;
  onIntrekken: () => void;
}) {
  const [vraagt, setVraagt] = useState<"EERDER" | "LATER" | null>(null);
  const [reden, setReden] = useState("");

  if (rij.bijstelling) {
    const tot = new Date(rij.bijstelling.vervaltOp).toLocaleDateString("nl-NL", {
      dateStyle: "short",
    });
    return (
      <div className="flex flex-col items-end gap-0.5">
        <span className="text-xs text-neutral-700">
          {rij.bijstelling.richting === "EERDER" ? "Door jou vooraan" : "Door jou achteraan"}
        </span>
        <span className="max-w-[16rem] truncate text-xs text-neutral-500" title={rij.bijstelling.reden}>
          {rij.bijstelling.reden}
        </span>
        <span className="text-xs text-neutral-400">vervalt {tot}</span>
        <button
          type="button"
          disabled={bezig}
          onClick={onIntrekken}
          className="text-xs text-brand-700 hover:underline disabled:opacity-50"
        >
          Ongedaan maken
        </button>
      </div>
    );
  }

  if (vraagt) {
    return (
      <div className="flex flex-col items-end gap-1">
        <label className="sr-only" htmlFor={`reden-${rij.id}`}>
          Waarom zet je dit dossier {vraagt === "EERDER" ? "vooraan" : "achteraan"}?
        </label>
        <input
          id={`reden-${rij.id}`}
          autoFocus
          value={reden}
          onChange={(e) => setReden(e.target.value)}
          placeholder={vraagt === "EERDER" ? "Waarom nu?" : "Waarom niet nu?"}
          className="w-56 rounded border border-neutral-300 px-2 py-1 text-xs"
        />
        <div className="flex gap-2">
          <button
            type="button"
            disabled={bezig || reden.trim().length < 4}
            onClick={() => {
              onBijstellen(vraagt, reden.trim());
              setVraagt(null);
              setReden("");
            }}
            className="rounded bg-brand-700 px-2 py-1 text-xs text-white disabled:opacity-40"
          >
            Opslaan
          </button>
          <button
            type="button"
            onClick={() => {
              setVraagt(null);
              setReden("");
            }}
            className="text-xs text-neutral-600 hover:underline"
          >
            Annuleren
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={() => setVraagt("EERDER")}
        className="text-xs text-neutral-600 hover:text-brand-700 hover:underline"
        title="Zet dit dossier vooraan in jouw lijst"
      >
        Nu oppakken
      </button>
      <button
        type="button"
        onClick={() => setVraagt("LATER")}
        className="text-xs text-neutral-600 hover:text-brand-700 hover:underline"
        title="Zet dit dossier achteraan in jouw lijst"
      >
        Niet nu
      </button>
    </div>
  );
}

/**
 * De redenen achter de plaats in de lijst.
 *
 * Dit is wat de score vervangt. Een getal van 0 tot 100 met een label als
 * "Onmiddellijk contact opnemen" beloofde een oordeel dat we niet kunnen
 * onderbouwen — bij realistisch volume kwam negentig procent van de dossiers in
 * die categorie. Een volgorde met de signalen erbij belooft alleen dit: hier zou
 * ik vandaag beginnen, en dit is waarom.
 */
function Redenen({ redenen }: { redenen: ClientRow["redenen"] }) {
  if (redenen.length === 0) {
    return <span className="text-xs text-neutral-400">Geen signalen</span>;
  }
  // Twee in beeld; de rest achter een titel, zodat de rij leesbaar blijft.
  const zichtbaar = redenen.slice(0, 2);
  const rest = redenen.slice(2);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {zichtbaar.map((r) => (
        <span
          key={r.sleutel}
          className="inline-block rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
        >
          {r.omschrijving}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          className="text-xs text-neutral-500"
          title={rest.map((r) => r.omschrijving).join("\n")}
        >
          +{rest.length}
        </span>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "red" | "amber" | "green";
}) {
  const colors = {
    neutral: "bg-white",
    red: "bg-red-50 border-red-200",
    amber: "bg-amber-50 border-amber-200",
    green: "bg-green-50 border-green-200",
  };
  return (
    <div className={`px-4 py-3 rounded-xl border ${colors[tone]}`}>
      <p className="text-xs text-neutral-600">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}

function ThreatPills({ counts }: { counts: ClientRow["threats"] }) {
  if (counts.total === 0) return <span className="text-xs text-neutral-400">–</span>;
  return (
    <div className="inline-flex gap-1 text-xs">
      {counts.critical > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-800 font-medium">
          {counts.critical}
        </span>
      )}
      {counts.high > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-800">{counts.high}</span>
      )}
      {counts.medium > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800">{counts.medium}</span>
      )}
    </div>
  );
}
